import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Typography, Box, Switch, FormControlLabel } from '@mui/material';
import { SocorristaParams } from '../../socorristas/api';
import AddPartnerDialog from './AddPartnerDialog';
import { supabase, supabaseAdminClient } from '../../Supabase/supabaseRealtimeClient';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Interface alinhada com a tabela socorrista
interface Socorrista {
  id: number;
  user_id: string;
  nome: string;
  telefone: string;
  email?: string;
  nome_empresa: string;
  status: string;
  data_cadastro: string;
}

const PartnerList: React.FC = () => {
  const [socorristas, setSocorristas] = useState<SocorristaParams[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSocorrista, setEditSocorrista] = useState<SocorristaParams | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // =============================
  // ÍNDICE DO ARQUIVO
  // 16: PartnerList (componente principal)
  // 26: useEffect - Carregamento de socorristas
  // 50: handleEdit
  // 55: handleDialogClose
  // 76: handleSaveEdit
  // 101: handleCreateSocorrista
  // 132: handleOpenDeleteDialog
  // 138: handleCloseDeleteDialog
  // 143: handleConfirmDelete
  // =============================

  // Função para buscar socorristas via API
  const fetchSocorristasFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      if (!url || !serviceKey) throw new Error('REACT_APP_SUPABASE_URL ou REACT_APP_SUPABASE_SERVICE_KEY não definida no .env');
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${url}/rest/v1/socorrista`, {
        method: 'GET',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      let data = await response.json();
      if (!response.ok) throw new Error(data.error_description || data.message || `Erro ${response.status}`);
      const dataSorted = data.sort((a: SocorristaParams, b: SocorristaParams) => {
        if (a.criado_em && b.criado_em) {
          return new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime();
        }
        return (b.id || 0) - (a.id || 0);
      })
      const socorristasNaoDeletados = dataSorted.filter((item: SocorristaParams) => !item.deletado);
      console.log('📋 Dados dos socorristas carregados:', socorristasNaoDeletados);
      setSocorristas(socorristasNaoDeletados);
    } catch (error) {
      console.error('Erro ao carregar socorristas:', error);
      setError('Não foi possível carregar a lista de socorristas. Por favor, tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocorristasFromAPI();
    const channel = supabase
      .channel('public:socorrista')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'socorrista' }, (payload) => {
        // Atualiza apenas o item específico que foi alterado
        if (payload.eventType === 'UPDATE' && payload.new) {
          setSocorristas(prevSocorristas =>
            prevSocorristas.map(socorrista =>
              socorrista.id === payload.new.id ? { ...socorrista, ...payload.new } as SocorristaParams : socorrista
            )
          );
        } else if (payload.eventType === 'INSERT' && payload.new) {
          setSocorristas(prevSocorristas => [payload.new as SocorristaParams, ...prevSocorristas]);
        } else if (payload.eventType === 'DELETE' && payload.old) {
          setSocorristas(prevSocorristas =>
            prevSocorristas.filter(socorrista => socorrista.id !== payload.old.id)
          );
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleEdit = (socorrista: SocorristaParams) => {
    setEditSocorrista(socorrista);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditSocorrista(null);
  };

  const handleSaveEdit = async (data: { empresa: string; contato: string; email: string; telefone: string }) => {
    if (!editSocorrista) return;
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      if (!url || !serviceKey) throw new Error('REACT_APP_SUPABASE_URL ou REACT_APP_SUPABASE_SERVICE_KEY não definida no .env');
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${url}/rest/v1/socorrista?id=eq.${editSocorrista.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          nome_empresa: data.empresa,
          nome: data.contato,
          email: data.email,
          telefone: data.telefone
        })
      });
      if (!response.ok) {
        const respData = await response.json().catch(() => ({}));
        throw new Error(respData.error_description || respData.message || `Erro ${response.status}`);
      }
      await fetchSocorristasFromAPI();
      setDialogOpen(false);
      setEditSocorrista(null);
      alert('Socorrista atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar socorrista.');
    }
  };

  const handleDelete = async (socorrista: SocorristaParams) => {
    if (!socorrista) return
    try {
      const { error: updateError } = await supabaseAdminClient
        .from('socorrista')
        .update({ deletado: true, email: `usuariodeletado${socorrista.id}@delete.com` })
        .eq('id', socorrista.id);

      if (updateError) throw updateError;

      if (socorrista.user_id) {
        const { error: authError } = await supabaseAdminClient.auth.admin.deleteUser(socorrista.user_id.toString());
        if (authError) {
           console.error('Erro ao excluir do Auth:', authError);
        }
      }

      await fetchSocorristasFromAPI()
      alert("Socorrista deletado com sucesso!")
    } catch (error) {
      alert("Erro ao deletar socorrista.")
      console.error(error)
    }
  }

  const handleToggleStatus = async (socorrista: SocorristaParams) => {
    try {
      console.log('🔍 Status atual do socorrista:', socorrista.status);
      setUpdatingStatus(socorrista.id || 0);
      const url = process.env.REACT_APP_SUPABASE_URL;
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
      if (!url || !serviceKey) throw new Error('REACT_APP_SUPABASE_URL ou REACT_APP_SUPABASE_SERVICE_KEY não definida no .env');
      const accessToken = localStorage.getItem('accessToken');

      const newStatus = socorrista.status?.toLowerCase() === 'ativo' ? 'inativo' : 'ativo';

      const response = await fetch(`${url}/rest/v1/socorrista?id=eq.${socorrista.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const respData = await response.json().catch(() => ({}));
        throw new Error(respData.error_description || respData.message || `Erro ${response.status}`);
      }
      // Não recarrega a lista nem mostra alert
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      // Não mostra alert
    } finally {
      setUpdatingStatus(null);
    }
  };

  // 5. Renderização da tabela e diálogos
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="tabela de socorristas">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Nome</TableCell>
            {isDesktop && <TableCell>Email</TableCell>}
            {isDesktop && <TableCell>Telefone</TableCell>}
            {isDesktop && <TableCell>Status</TableCell>}
            {isDesktop && <TableCell>Empresa</TableCell>}
            <TableCell align='center' sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {socorristas.length > 0 ? (
            socorristas.map((socorrista) => (
              <TableRow key={socorrista.id}>
                <TableCell>{socorrista.id}</TableCell>
                <TableCell>{socorrista.nome}</TableCell>
                {isDesktop && <TableCell>{socorrista.email}</TableCell>}
                {isDesktop && <TableCell>{socorrista.telefone}</TableCell>}
                {isDesktop && <TableCell>{socorrista.status}</TableCell>}
                {isDesktop && <TableCell>{socorrista.nome_empresa}</TableCell>}
                <TableCell align='center' sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                  <Button size="small" color="error" onClick={() => handleDelete(socorrista)}>Excluir</Button>
                  <Button size="small" color="primary" onClick={() => handleEdit(socorrista)}>Editar</Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={socorrista.status?.toLowerCase() === 'ativo'}
                        onChange={() => handleToggleStatus(socorrista)}
                        disabled={updatingStatus === socorrista.id}
                        size="small"
                      />
                    }
                    label={socorrista.status?.toLowerCase() === 'ativo' ? 'Ativo' : 'Inativo'}
                    sx={{ ml: 1 }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={isDesktop ? 7 : 3} align="center" sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>
                Nenhum socorrista encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <AddPartnerDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        initialData={editSocorrista ? {
          empresa: editSocorrista.nome_empresa || '',
          contato: editSocorrista.nome || '',
          email: editSocorrista.email || '',
          telefone: editSocorrista.telefone || '',
        } : undefined}
        onSave={editSocorrista ? handleSaveEdit : undefined}
      />

    </TableContainer>
  );
};

export default PartnerList; 
